/*!
 * SkyPlus Unified Search API client
 * Location: app/src/app/components/skyplus/search/wf-search-api.service.ts
 *
 * Round 6 — client for the backend's unified repository search endpoint
 * (POST /api/wf/search). The backend resolves document types from the
 * registry (aspects + field types), forwards the caller's bearer token to
 * Alfresco (so permission filtering applies), and runs a configurable
 * engine strategy chain:
 *
 *   - afts : rich Solr-backed search (full text, wildcards)
 *   - cmis : transactional (database) metadata queries — no Solr required
 *   - auto : AFTS first, CMIS fallback on failure OR empty results
 *
 * Configured via the `search.engine` system setting (default: auto).
 *
 * Entries in the response are raw Alfresco node entry objects (id, name,
 * properties, path, content, …) — identical to what the Search API returns
 * inside list.entries[].entry — so existing node-mapping code in the
 * search page and document library works unchanged.
 *
 * Consumers keep their legacy direct-to-Alfresco code path as a fallback:
 * if this endpoint is unreachable (e.g. older backend), searching still
 * works exactly as before.
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';

/** Intent payload — mirrors the backend's WfSearchDtos.SearchRequest. */
export interface WfSearchRequest {
  /** Free-text term (file/folder name, title, description, content). */
  term?: string;
  /** Registry document type key — backend resolves aspects + field types. */
  documentTypeKey?: string;
  /** Legacy static types: the (single) aspect to search. */
  aspectName?: string;
  /** propertyName → entered value. */
  fields?: Record<string, string>;
  /** propertyName → text|date|number|select (legacy static types only). */
  fieldTypes?: Record<string, string>;
  /** Created-date range, yyyy-MM-dd inclusive. */
  dateFrom?: string;
  dateTo?: string;
  /** Scope to this folder's subtree (node id). */
  folderId?: string;
  includeSubfolders?: boolean;
  /** Include cm:folder hits in simple-search results. */
  includeFolders?: boolean;
  maxItems?: number;
  skipCount?: number;
}

export interface WfSearchResponse {
  /** Engine that produced the result: 'afts' | 'cmis'. */
  engine: string;
  totalItems: number;
  /** Raw Alfresco node entries. */
  entries: any[];
}

/** Field-value autocomplete request — mirrors WfSearchDtos.ValuesRequest. */
export interface WfValuesRequest {
  documentTypeKey?: string;
  aspectName?: string;
  /** The property whose distinct values to suggest (e.g. eacc:memoTo). */
  propertyName: string;
  /** What the user has typed so far (substring filter). */
  prefix?: string;
  folderId?: string;
  includeSubfolders?: boolean;
  maxItems?: number;
}

export interface WfValuesResponse {
  engine: string;
  values: string[];
}

@Injectable({ providedIn: 'root' })
export class WfSearchApiService {

  private readonly base: string;

  constructor(
    private http: HttpClient,
    private auth: AuthenticationService,
    appConfig: AppConfigService
  ) {
    this.base = `${appConfig.get<string>('skyplus.apiUrl', '/bpm/api')}/wf/search`;
  }

  search(request: WfSearchRequest): Observable<WfSearchResponse> {
    return this.http.post<WfSearchResponse>(this.base, request, { headers: this.headers() });
  }

  /** Field-value autocomplete — distinct indexed values for one property. */
  values(request: WfValuesRequest): Observable<WfValuesResponse> {
    return this.http.post<WfValuesResponse>(`${this.base}/values`, request, { headers: this.headers() });
  }

  private headers(): HttpHeaders {
    const token = this.auth.getToken();
    const base: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) { base['Authorization'] = `Bearer ${token}`; }
    return new HttpHeaders(base);
  }
}
